import type { HTMLAttributes, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { DefaultTheme } from 'styled-components';
import { X } from '@phosphor-icons/react';

import { disableScrolling, enableScrolling } from '@tmh-website/services/scroll.service';
import { Color } from '@tmh-website/shared/colors';

import NavigationWatcher from './NavigationWatcher';
import * as Styled from './Popup.styles';
import { PopupThemeColor, popupThemes } from './Popup.theme';

export interface PopupProps {
  themeColor: PopupThemeColor;
  children: ReactNode;
  isOpen: boolean;
  hasCloseButton?: boolean;
  onClose?: () => void;
  borderRadius?: keyof DefaultTheme['borderRadius'];
  noPadding?: boolean;

  /**
   * Set a custom max width for the popup
   */
  maxWidth?: string;

  /**
   * Choose the layout for the popup. regular is a normal popup, fullscreen is a popup that covers the whole screen,
   * fullscreen-centered is a popup that covers the whole screen and has its contents centered
   */
  layout?: 'regular' | 'fullscreen' | 'fullscreen-centered';

  contentDisplay?: 'flex' | 'block';
  flexDirection?: 'column' | 'column-reverse';

  containerProps?: HTMLAttributes<HTMLDivElement> & {
    [key: `data-${string}`]: string;
  };

  /**
   * Determines whether the popup window should be persistent
   * (should NOT allow the User to close it by clicking outside of it).
   */
  isPersistent?: boolean;

  /**
   * Enable this flag when this component is used outside of the NextJS context.
   */
  isStandaloneMode?: boolean;
}

const Popup = ({
  themeColor = Color.Neutral_0_White,
  isOpen,
  hasCloseButton,
  onClose = () => {},
  children,
  borderRadius,
  noPadding,
  maxWidth,
  layout = 'regular',
  contentDisplay = 'flex',
  flexDirection = 'column-reverse',
  containerProps = {},
  isPersistent = false,
  isStandaloneMode,
}: PopupProps) => {
  const [isInDOM, setIsInDOM] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const [visualViewport, setVisualViewport] = useState<Partial<
    Pick<VisualViewport, 'offsetTop' | 'offsetLeft' | 'width' | 'height'>
  > | null>(null);

  const updateVisualViewport = (): void => {
    setVisualViewport({
      offsetTop: window.visualViewport ? Math.round(window.visualViewport.offsetTop) : undefined,
      offsetLeft: window.visualViewport ? Math.round(window.visualViewport.offsetLeft) : undefined,
      width: window.visualViewport?.width,
      height: window.visualViewport?.height,
    });
  };

  /**
   * Toggle modal with a delay to enable css transistions
   */
  useEffect(() => {
    let timeout: number;

    if (isOpen) {
      disableScrolling();
      updateVisualViewport();

      timeout = window.setTimeout(() => {
        setIsInDOM(true);
        setIsVisible(true);
      }, 10);
    } else {
      enableScrolling();
      setIsVisible(false);

      timeout = window.setTimeout(() => setIsInDOM(false), 300);
    }

    return () => {
      enableScrolling();
      window.clearTimeout(timeout);
    };
  }, [isOpen]);

  /**
   * Enable close with keyboard
   */
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Set correct position and height on mobile devices
   * This has to be done because the viewport size differs from the rendered height
   */
  useEffect(() => {
    if (!isOpen || !window.visualViewport) {
      return () => {};
    }

    updateVisualViewport();

    const onResize = (): void => {
      updateVisualViewport();
    };

    window.visualViewport.addEventListener('resize', onResize);

    return () => {
      window.visualViewport?.removeEventListener('resize', onResize);
    };
  }, [isOpen]);

  if (!isOpen && !isInDOM) {
    return null;
  }

  /**
   * Handles the `onClick` event fired by the `<Styled.Fade>`.
   */
  const handleFadeClick = (): void => {
    if (isPersistent) {
      return;
    }

    onClose();
  };

  return (
    <Styled.Modal
      aria-hidden={!isVisible}
      $layout={layout}
      style={{
        top: visualViewport?.offsetTop ? `${visualViewport?.offsetTop}px` : 0,
        left: visualViewport?.offsetLeft ? `${visualViewport?.offsetLeft}px` : 0,
        width: visualViewport?.width ? `${visualViewport?.width}px` : '100vw',
        height: visualViewport?.height ? `${visualViewport?.height}px` : '100vw',
      }}>
      <Styled.Container
        $themeColor={themeColor}
        $isVisible={isVisible}
        $borderRadius={borderRadius ?? 'small'}
        $noPadding={noPadding}
        $layout={layout}
        $maxWidth={maxWidth}
        $display={contentDisplay}
        $flexDirection={flexDirection}
        {...containerProps}>
        {hasCloseButton && (
          <Styled.CloseButton onClick={onClose} phosphorPadding themeColor={popupThemes[themeColor].buttonThemeColor}>
            <X size='24' />
          </Styled.CloseButton>
        )}
        {children}
      </Styled.Container>

      <Styled.Fade $isVisible={isVisible} onClick={handleFadeClick} />

      {!isStandaloneMode && <NavigationWatcher onNavigate={() => onClose()} />}
    </Styled.Modal>
  );
};

export default Popup;
